import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  RefreshCw, 
  Sparkles, 
  Info, 
  TrendingDown, 
  AlertTriangle,
  ChevronRight,
  X
} from 'lucide-react';
import { DashboardFilter, AIInsightsModal, type DashboardFilterOption } from '@/components/dashboard';

// Mock data - replace with actual API calls
const mockData = {
  todaysPriorities: {
    collectionQueue: { count: 1, amount: '£18,229' },
    unallocatedPayments: { count: 4, amount: '£23,250' },
    promisesDueToday: { dueCount: 0, dueAmount: '£0', brokenCount: 11 },
    disputes: { count: 4, amount: '£33,920' }
  },
  agingPortfolio: {
    totalOutstanding: '£2,888,000',
    bands: [
      { label: '0-90 days', amount: '£2,200,000', percentage: 76 },
      { label: 'Over 90 days', amount: '£688,000', percentage: 24 },
      { label: 'Over 180 days', amount: '£318,000', percentage: 11 },
      { label: 'Over 365 days', amount: '£98,000', percentage: 3 }
    ]
  },
  performanceSnapshot: {
    todaysWins: {
      paymentsReceived: { count: 2, amount: '£8,200' },
      promisesKept: 5,
      casesResolved: 3
    },
    monthToDate: {
      collectionRate: {
        current: 55,
        target: 85,
        collectedAmount: '£2.3M',
        totalDueAmount: '£4.3M',
        pointsBelowTarget: 30
      },
      keyMetrics: {
        dso: { value: 42, target: 40 },
        promiseSuccess: { percentage: 55, kept: 22, total: 40 },
        avgPaymentDays: { current: 18, trend: { change: -3, direction: 'down' as const } }
      }
    }
  },
  riskAlerts: [
    {
      id: '1',
      type: 'strategy-inactive' as const,
      severity: 'warning' as const,
      primaryIssue: 'Collection Strategy Inactive',
      context: 'Post-Dispute Resolution paused • 45 customers affected • 0 emails sent in 14 days',
      clickUrl: '/settings/strategies'
    },
    {
      id: '2',
      type: 'customer-risk' as const,
      severity: 'warning' as const,
      customerName: 'Tech Solutions Inc',
      primaryIssue: 'Invoice aging 90+ days - £89,000',
      context: 'Moved from 60-90 days this week',
      clickUrl: '/customers/tech-solutions'
    },
    {
      id: '3',
      type: 'customer-risk' as const,
      severity: 'urgent' as const,
      customerName: 'Acme Corporation',
      primaryIssue: 'Broken promise - £25,000',
      context: 'Payment was due 3 days ago',
      clickUrl: '/customers/acme'
    },
    {
      id: '4',
      type: 'customer-risk' as const,
      severity: 'warning' as const,
      customerName: 'Global Industries Ltd',
      primaryIssue: 'Credit score decreased to 67',
      context: 'Down from 82 last month',
      clickUrl: '/customers/global-industries'
    }
  ],
  collectionStrategy: {
    totalInCollection: '£926K',
    strategies: [
      { id: '1', name: 'Standard 30-Day Follow-up', status: 'active' as const, amount: '£289K' },
      { id: '2', name: 'High-Risk Intensive', status: 'active' as const, amount: '£185K' },
      { id: '3', name: 'Post-Dispute Resolution', status: 'paused' as const, amount: '£0' },
      { id: '4', name: 'Final Notice Escalation', status: 'active' as const, amount: '£452K' }
    ]
  }
};

export function CreditControlDashboardV3() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<DashboardFilterOption>({ id: 'everyone', name: 'Everyone', type: 'all' });
  const [activeStrategyTab, setActiveStrategyTab] = useState<'all' | 'active' | 'paused'>('all');

  // Filter suffix for section headers
  const filterSuffix = selectedFilter.id !== 'everyone' ? ` - ${selectedFilter.name}` : '';

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsRefreshing(false);
  };

  // Filter strategies based on active tab
  const filteredStrategies = mockData.collectionStrategy.strategies.filter(strategy => {
    if (activeStrategyTab === 'all') return true;
    return strategy.status === activeStrategyTab;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 space-y-6">
        {/* Header Controls - Single Row */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>

            {/* AI Insights Button */}
            <Button
              onClick={() => setShowAIInsights(true)}
              className="gap-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
            >
              <Sparkles className="h-4 w-4" />
              AI Insights
              <span className="ml-2 px-2 py-0.5 text-xs bg-white/20 rounded-full">
                89% confidence
              </span>
            </Button>
          </div>

          {/* Filter Dropdown */}
          <DashboardFilter
            selectedFilter={selectedFilter}
            onFilterChange={setSelectedFilter}
          />
        </div>

        {/* Global Filter Banner */}
        {selectedFilter.id !== 'everyone' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                {selectedFilter.initials || selectedFilter.name.substring(0, 2).toUpperCase()}
              </div>
              <span className="text-sm font-medium text-gray-900">
                Viewing: {selectedFilter.name}'s Portfolio
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedFilter({ id: 'everyone', name: 'Everyone', type: 'all' })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Dashboard Title */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">Credit Control Dashboard</h1>
          <p className="text-sm text-gray-600">
            Real-time overview of outstanding invoices, collection performance, and team activity
          </p>
          <p className="text-sm text-gray-500">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Section 1: Today's Priorities - 4 Gateway Scorecards */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center text-white text-sm">◎</span>
            Today's Priorities
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Collection Queue Card */}
            <Link to="/collections/queue" className="block">
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      COLLECTION QUEUE
                    </p>
                    <p className="text-5xl font-bold text-gray-900">
                      {mockData.todaysPriorities.collectionQueue.count}
                    </p>
                    <p className="text-sm text-gray-600">
                      accounts requiring action
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {mockData.todaysPriorities.collectionQueue.amount}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* Unallocated Payments Card */}
            <Link to="/payments/allocate" className="block">
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      UNALLOCATED PAYMENTS
                    </p>
                    <p className="text-5xl font-bold text-gray-900">
                      {mockData.todaysPriorities.unallocatedPayments.count}
                    </p>
                    <p className="text-sm text-gray-600">
                      receipts pending
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {mockData.todaysPriorities.unallocatedPayments.amount}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* Promises Due Today Card */}
            <Link to="/collections/promises" className="block">
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      PROMISES DUE TODAY
                    </p>
                    <p className="text-5xl font-bold text-gray-900">
                      {mockData.todaysPriorities.promisesDueToday.dueCount}
                    </p>
                    <p className="text-sm text-gray-600">
                      due today
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {mockData.todaysPriorities.promisesDueToday.dueAmount} | {mockData.todaysPriorities.promisesDueToday.brokenCount} broken
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* Disputes Card */}
            <Link to="/disputes" className="block">
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      DISPUTES
                    </p>
                    <p className="text-5xl font-bold text-gray-900">
                      {mockData.todaysPriorities.disputes.count}
                    </p>
                    <p className="text-sm text-gray-600">
                      Unresolved Disputes
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {mockData.todaysPriorities.disputes.amount}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>

        {/* Section 2: Aging Portfolio + Performance Snapshot (Side by Side) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Aging Portfolio */}
          <Card>
            <CardHeader>
              <CardTitle>Aging Portfolio{filterSuffix}</CardTitle>
              <CardDescription>
                {mockData.agingPortfolio.totalOutstanding} total outstanding
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {mockData.agingPortfolio.bands.map((band, index) => (
                <Link
                  key={index}
                  to={`/analytics-reporting/ageing-analysis?filter=${band.label}`}
                  className="block group"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{band.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-gray-900">{band.amount}</span>
                        <span className="text-sm text-gray-500">({band.percentage}%)</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-teal-500 h-2 rounded-full transition-all group-hover:bg-teal-600"
                        style={{ width: `${band.percentage}%` }}
                      />
                    </div>
                  </div>
                </Link>
              ))}
              
              <div className="pt-4 border-t flex justify-end">
                <Link
                  to="/analytics-reporting/ageing-analysis"
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 hover:underline"
                >
                  See your aging debt
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Performance Snapshot */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Snapshot{filterSuffix}</CardTitle>
              <CardDescription>
                {selectedFilter.id !== 'everyone' ? `${selectedFilter.name}'s achievements today` : 'Your achievements today'}
              </CardDescription>
              {/* Updated design to match Aging Portfolio */}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Today's Wins */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  Today's Wins
                </h3>
                <div className="space-y-2">
                  <Link to="/payments/history" className="flex items-center justify-between py-2 hover:bg-gray-50 rounded px-2 -mx-2">
                    <span className="text-sm text-gray-600">Payments Received</span>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-gray-900">
                        {mockData.performanceSnapshot.todaysWins.paymentsReceived.count} • {mockData.performanceSnapshot.todaysWins.paymentsReceived.amount}
                      </span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                  </Link>
                  <Link to="/collections/promises" className="flex items-center justify-between py-2 hover:bg-gray-50 rounded px-2 -mx-2">
                    <span className="text-sm text-gray-600">Promises Kept</span>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-gray-900">
                        {mockData.performanceSnapshot.todaysWins.promisesKept}
                      </span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                  </Link>
                  <Link to="/collections/queue" className="flex items-center justify-between py-2 hover:bg-gray-50 rounded px-2 -mx-2">
                    <span className="text-sm text-gray-600">Cases Resolved</span>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-gray-900">
                        {mockData.performanceSnapshot.todaysWins.casesResolved}
                      </span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                  </Link>
                </div>
              </div>

              {/* Month-to-Date Performance */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Month-to-Date Performance
                </h3>

                {/* Collection Rate */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">Collection Rate</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="space-y-2">
                              <p className="font-semibold">Collection Rate Calculation:</p>
                              <p className="text-sm">Cash collected this month ÷ Total cash due this month</p>
                              <p className="text-sm">
                                {mockData.performanceSnapshot.monthToDate.collectionRate.collectedAmount} ÷{' '}
                                {mockData.performanceSnapshot.monthToDate.collectionRate.totalDueAmount} ={' '}
                                {mockData.performanceSnapshot.monthToDate.collectionRate.current}%
                              </p>
                              <p className="text-sm">
                                <strong>Target:</strong> {mockData.performanceSnapshot.monthToDate.collectionRate.target}% (industry benchmark)
                              </p>
                              <p className="text-sm">
                                <strong>Current gap:</strong> {mockData.performanceSnapshot.monthToDate.collectionRate.pointsBelowTarget} percentage points below target
                              </p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <span className="text-[32px] font-bold text-gray-900">
                      {mockData.performanceSnapshot.monthToDate.collectionRate.current}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-teal-500 h-2 rounded-full"
                      style={{ width: `${mockData.performanceSnapshot.monthToDate.collectionRate.current}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-600 flex items-center gap-1">
                    Target: {mockData.performanceSnapshot.monthToDate.collectionRate.target}% •{' '}
                    {mockData.performanceSnapshot.monthToDate.collectionRate.pointsBelowTarget} points below target
                    <AlertTriangle className="h-3 w-3 text-orange-500" />
                  </p>
                </div>

                {/* Key Metrics */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Key Metrics</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* DSO */}
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">DSO</p>
                      <p className="text-xl font-bold text-gray-900">
                        {mockData.performanceSnapshot.monthToDate.keyMetrics.dso.value}
                      </p>
                      <p className="text-xs text-gray-600">days</p>
                      <p className="text-xs text-orange-600 flex items-center gap-1 mt-1">
                        Target: {mockData.performanceSnapshot.monthToDate.keyMetrics.dso.target}
                        <AlertTriangle className="h-3 w-3" />
                      </p>
                    </div>

                    {/* Promise Success */}
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Promise Success</p>
                      <p className="text-xl font-bold text-gray-900">
                        {mockData.performanceSnapshot.monthToDate.keyMetrics.promiseSuccess.percentage}%
                      </p>
                      <p className="text-xs text-gray-600">
                        {mockData.performanceSnapshot.monthToDate.keyMetrics.promiseSuccess.kept} kept of{' '}
                        {mockData.performanceSnapshot.monthToDate.keyMetrics.promiseSuccess.total} total
                      </p>
                      <p className="text-xs text-gray-600 mt-1">(this month)</p>
                    </div>

                    {/* Avg Payment Days */}
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Avg Payment Days</p>
                      <p className="text-xl font-bold text-gray-900">
                        {mockData.performanceSnapshot.monthToDate.keyMetrics.avgPaymentDays.current}
                      </p>
                      <p className="text-xs text-gray-600">days</p>
                      <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                        {Math.abs(mockData.performanceSnapshot.monthToDate.keyMetrics.avgPaymentDays.trend.change)} days faster
                        <TrendingDown className="h-3 w-3" />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section 3: Risk Alerts + Collection Strategy (Side by Side) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Risk Alerts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Risk Alerts{filterSuffix}
              </CardTitle>
              <CardDescription>
                {selectedFilter.id !== 'everyone' 
                  ? `Customer-specific risks in ${selectedFilter.name}'s portfolio`
                  : 'Customer-specific risks requiring investigation'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockData.riskAlerts.map((alert) => (
                  <Link
                    key={alert.id}
                    to={alert.clickUrl}
                    className={`block p-4 rounded-lg border-l-4 transition-all hover:shadow-md ${
                      alert.severity === 'urgent'
                        ? 'bg-red-50 border-red-500'
                        : 'bg-orange-50 border-orange-500'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        {alert.type === 'strategy-inactive' ? (
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <AlertTriangle className="h-4 w-4 text-orange-600" />
                              <p className="font-semibold text-gray-900">{alert.primaryIssue}</p>
                            </div>
                            <p className="text-sm text-gray-700">{alert.context}</p>
                          </>
                        ) : (
                          <>
                            <p className="font-semibold text-gray-900 mb-1">{alert.customerName}</p>
                            <p className="text-sm text-gray-700 mb-1">{alert.primaryIssue}</p>
                            <p className="text-xs text-gray-600">{alert.context}</p>
                          </>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Collection Strategy */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Collection Strategy{filterSuffix}</CardTitle>
                  <CardDescription>
                    {selectedFilter.id !== 'everyone'
                      ? `Strategies affecting ${selectedFilter.name}'s accounts`
                      : 'Active strategies and performance'}
                  </CardDescription>
                </div>
                <Link to="/settings/strategies" className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                  See More
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Donut Chart Placeholder */}
              <div className="flex flex-col items-center justify-center py-6">
                <svg width="200" height="200" viewBox="0 0 200 200">
                  <circle cx="100" cy="100" r="80" fill="none" stroke="#14B8A6" strokeWidth="30" strokeDasharray="155 500" />
                  <circle cx="100" cy="100" r="80" fill="none" stroke="#EF4444" strokeWidth="30" strokeDasharray="122 500" strokeDashoffset="-155" />
                  <circle cx="100" cy="100" r="80" fill="none" stroke="#F59E0B" strokeWidth="30" strokeDasharray="63 500" strokeDashoffset="-277" />
                  <text x="100" y="95" textAnchor="middle" className="text-2xl font-bold" fill="#111827">
                    {mockData.collectionStrategy.totalInCollection}
                  </text>
                  <text x="100" y="115" textAnchor="middle" className="text-xs" fill="#6B7280">
                    Total in Active
                  </text>
                  <text x="100" y="128" textAnchor="middle" className="text-xs" fill="#6B7280">
                    Collection
                  </text>
                </svg>
              </div>

              {/* Tabs */}
              <div className="flex gap-4 border-b">
                <button
                  onClick={() => setActiveStrategyTab('all')}
                  className={`pb-2 text-sm font-medium transition-colors ${
                    activeStrategyTab === 'all'
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setActiveStrategyTab('active')}
                  className={`pb-2 text-sm font-medium transition-colors ${
                    activeStrategyTab === 'active'
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setActiveStrategyTab('paused')}
                  className={`pb-2 text-sm font-medium transition-colors ${
                    activeStrategyTab === 'paused'
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Paused
                </button>
              </div>

              {/* Strategy List */}
              <div className="space-y-2">
                {filteredStrategies.map((strategy) => (
                  <Link
                    key={strategy.id}
                    to={`/settings/strategies/${strategy.id}`}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded ${
                          strategy.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {strategy.status.toUpperCase()}
                      </span>
                      <span className="text-sm font-medium text-gray-900">{strategy.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{strategy.amount}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* AI Insights Modal */}
      <AIInsightsModal
        isOpen={showAIInsights}
        onClose={() => setShowAIInsights(false)}
      />
    </div>
  );
}
